<template>
  <div class="function-page">
    <el-card shadow="never">
      <template #header>
        <div class="page-header">
          <span class="page-title">功能管理</span>
          <el-button type="primary" :icon="Plus" @click="openDialog()">新增功能</el-button>
        </div>
      </template>
      <el-form inline class="search-form" @submit.prevent>
        <el-form-item label="功能名称"><el-input v-model="search.name" clearable /></el-form-item>
        <el-form-item label="状态"><el-select v-model="search.status" clearable style="width: 140px"><el-option label="启用" :value="1" /><el-option label="停用" :value="0" /></el-select></el-form-item>
        <el-form-item><el-button :icon="Refresh" :loading="loading" @click="loadData">刷新</el-button></el-form-item>
      </el-form>
      <el-table :data="visibleFunctions" row-key="id" border v-loading="loading">
        <el-table-column prop="name" label="功能名称" min-width="140" />
        <el-table-column label="功能类型" min-width="120"><template #default="{ row }">{{ typeName(row.code) }}</template></el-table-column>
        <el-table-column label="图标" width="80" align="center"><template #default="{ row }"><el-image v-if="row.icon" :src="row.icon" fit="contain" class="function-icon"><template #error>不可用</template></el-image><span v-else>默认</span></template></el-table-column>
        <el-table-column label="状态" width="90"><template #default="{ row }"><el-tag :type="row.status === 1 ? 'success' : 'info'">{{ row.status === 1 ? '启用' : '停用' }}</el-tag></template></el-table-column>
        <el-table-column label="首页入口" width="105"><template #default="{ row }">{{ row.showHome === 1 ? '显示' : '隐藏' }}</template></el-table-column>
        <el-table-column label="记录入口" width="105"><template #default="{ row }">{{ row.showRecords === 1 ? '显示' : '隐藏' }}</template></el-table-column>
        <el-table-column prop="sortOrder" label="排序" width="85" />
        <el-table-column prop="updateTime" label="更新时间" min-width="170" />
        <el-table-column label="操作" width="230" fixed="right">
          <template #default="{ row }">
            <el-button size="small" :icon="Edit" @click="openDialog(row)">编辑</el-button>
            <el-button size="small" :loading="changingId === row.id" @click="toggleStatus(row)">{{ row.status === 1 ? '停用' : '启用' }}</el-button>
            <el-tooltip content="删除功能"><el-button size="small" type="danger" :icon="Delete" aria-label="删除功能" @click="remove(row)" /></el-tooltip>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
    <el-dialog v-model="dialog.visible" :title="dialog.form.id ? '编辑功能' : '新增功能'" width="580px">
      <el-form :model="dialog.form" label-width="110px">
        <el-form-item label="功能类型" required><el-select v-model="dialog.form.code" :disabled="!!dialog.form.id" style="width: 100%" @change="selectType"><el-option v-for="item in FUNCTION_OPTIONS" :key="item.value" :label="item.label" :value="item.value" :disabled="!dialog.form.id && functions.some(row => row.code === item.value)" /></el-select></el-form-item>
        <el-form-item label="功能名称" required><el-input v-model="dialog.form.name" maxlength="64" /></el-form-item>
        <el-form-item label="图标地址"><el-input v-model="dialog.form.icon" clearable maxlength="512" /></el-form-item>
        <el-form-item label="启用功能"><el-switch v-model="dialog.form.status" :active-value="1" :inactive-value="0" /></el-form-item>
        <el-form-item label="首页入口"><el-switch v-model="dialog.form.showHome" :active-value="1" :inactive-value="0" /></el-form-item>
        <el-form-item label="记录入口"><el-switch v-model="dialog.form.showRecords" :active-value="1" :inactive-value="0" /></el-form-item>
        <el-form-item label="排序"><el-input-number v-model="dialog.form.sortOrder" :min="0" :max="2147483647" :precision="0" /></el-form-item>
      </el-form>
      <template #footer><el-button @click="dialog.visible = false">取消</el-button><el-button type="primary" :loading="saving" @click="save">保存</el-button></template>
    </el-dialog>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { Plus, Refresh, Edit, Delete } from "@element-plus/icons-vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { FUNCTION_OPTIONS, getFunctions, saveFunction, updateFunctionStatus, deleteFunction } from "@/api/function";

const functions = ref([]);
const loading = ref(false);
const saving = ref(false);
const changingId = ref(null);
const search = reactive({ name: "", status: "" });
const dialog = reactive({ visible: false, form: {} });
const visibleFunctions = computed(() => functions.value.filter(row => row.name.includes(search.name.trim()) &&
  (search.status === "" || search.status === undefined || row.status === search.status)));
const typeName = code => FUNCTION_OPTIONS.find(item => item.value === code)?.label || "未知功能";

async function loadData() {
  loading.value = true;
  try {
    const res = await getFunctions();
    if (res.code === 200) functions.value = res.data || [];
  } finally { loading.value = false; }
}
function openDialog(row) {
  dialog.form = row ? { id: row.id, name: row.name, code: row.code, icon: row.icon || "", status: row.status,
    showHome: row.showHome, showRecords: row.showRecords, sortOrder: row.sortOrder } :
    { name: "", code: "", icon: "", status: 0, showHome: 0, showRecords: 0, sortOrder: 0 };
  dialog.visible = true;
}
function selectType(code) {
  if (!dialog.form.name) dialog.form.name = typeName(code);
}
async function save() {
  if (!dialog.form.code || !dialog.form.name?.trim()) return ElMessage.warning("请选择功能类型并填写名称");
  if (dialog.form.icon && !/^https?:\/\//i.test(dialog.form.icon.trim())) return ElMessage.warning("请填写有效的图片网址");
  saving.value = true;
  try {
    const res = await saveFunction({ ...dialog.form, name: dialog.form.name.trim() });
    if (res.code === 200 && res.data === true) {
      ElMessage.success("保存成功");
      dialog.visible = false;
      await loadData();
    }
  } finally { saving.value = false; }
}
async function toggleStatus(row) {
  try {
    await ElMessageBox.confirm(row.status === 1 ? `停用“${row.name}”后将隐藏入口并停止小程序新下单，历史订单保留。` : `确认启用“${row.name}”？`, "确认", { type: "warning" });
  } catch { return; }
  changingId.value = row.id;
  try {
    const res = await updateFunctionStatus(row.id, row.status === 1 ? 0 : 1);
    if (res.code === 200 && res.data === true) { ElMessage.success("状态已更新"); await loadData(); }
  } finally { changingId.value = null; }
}
async function remove(row) {
  try { await ElMessageBox.confirm(`确认删除功能“${row.name}”？`, "确认", { type: "warning" }); } catch { return; }
  const res = await deleteFunction(row.id);
  if (res.code === 200 && res.data === true) { ElMessage.success("已删除"); await loadData(); }
}
onMounted(loadData);
</script>

<style scoped lang="scss">
.function-page { padding: 16px; }
.page-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.page-title { font-size: 18px; font-weight: 600; }
.function-icon { width: 32px; height: 32px; }
</style>
